import React, { useMemo } from "react";
import BaseTableAction from "../../Base/BaseTableAction";
import BaseSelect from "../../Base/BaseSelect";
import {
  ServiceEmailRequest,
  ServiceEmailStatus,
} from "../../../models/ServiceEmailRequestModel";
import "./styles/ServicesTable.css";
import "./styles/ServiceEmailsTable.css";
import "./styles/ServiceEmailSolicitudesTable.css";

export type ServiceEmailsTableFilters = {
  search: string;
  status: "" | ServiceEmailStatus;
  received_date: string;
  request_date: string;
  company_id: string;
  service_id: string;
};

type CompanyOption = { value: string; label: string };

type Props = {
  data: ServiceEmailRequest[];
  totalCount?: number;
  loading?: boolean;
  filters: ServiceEmailsTableFilters;
  companies?: CompanyOption[];
  onFiltersChange: (filters: ServiceEmailsTableFilters) => void;
  onEdit: (item: ServiceEmailRequest) => void;
  onStatusChange: (item: ServiceEmailRequest, status: ServiceEmailStatus) => void;
  onComment: (item: ServiceEmailRequest) => void;
};

const STATUS_LABELS: Record<ServiceEmailStatus, string> = {
  PENDIENTE: "Pendiente",
  AGREGADO: "Agregado",
};

const todayIso = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/** Fecha de calendario (YYYY-MM-DD) sin desfase de zona. */
const formatCalendarDay = (raw?: string | null) => {
  if (!raw) return "—";
  const iso = raw.slice(0, 10);
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return raw;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat("es-CL", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  }).format(date);
};

const formatReceived = (raw?: string | null) => {
  if (!raw) return { date: "—", time: "—" };
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return { date: raw, time: "" };
  return {
    date: new Intl.DateTimeFormat("es-CL", {
      day: "numeric",
      month: "short",
      year: "2-digit",
    }).format(date),
    time: new Intl.DateTimeFormat("es-CL", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date),
  };
};

const formatTime = (raw?: string | null) => {
  if (!raw) return "—";
  const match = String(raw).match(/^(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : raw;
};

const EditIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L9.75 16.902 6 18l1.098-3.75L16.862 4.487z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UndoIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M9 14L4 9l5-5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 9h10.5a5.5 5.5 0 010 11H11"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CommentIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M8 10h8M8 14h5M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 21l1.395-3.72C3.512 16.042 3 14.574 3 13c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ServiceEmailsTable: React.FC<Props> = ({
  data,
  totalCount,
  loading = false,
  filters,
  companies = [],
  onFiltersChange,
  onEdit,
  onStatusChange,
  onComment,
}) => {
  const resultsCount = totalCount ?? data.length;

  const pendingInView = useMemo(
    () =>
      data.filter(
        (item) =>
          Boolean(item.has_pending_service) || (item.pending_solicitudes_count ?? 0) > 0
      ).length,
    [data]
  );

  const updateFilter = <K extends keyof ServiceEmailsTableFilters>(
    key: K,
    value: ServiceEmailsTableFilters[K]
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilters = () =>
    onFiltersChange({
      search: "",
      status: "",
      received_date: todayIso(),
      request_date: "",
      company_id: "",
      service_id: "",
    });

  const hasFilters = Boolean(
    filters.search ||
      filters.status ||
      filters.company_id ||
      filters.service_id ||
      filters.request_date ||
      (filters.received_date && filters.received_date !== todayIso())
  );

  return (
    <div className="service-emails-list service-emails-list--v2">
      <div className="service-emails-list-toolbar">
        <div className="service-emails-list-toolbar-top">
          <div className="service-emails-list-title">
            <div className="service-emails-list-heading">
              <span className="service-emails-list-count">
                {resultsCount} correo{resultsCount !== 1 ? "s" : ""}
              </span>
              {pendingInView > 0 ? (
                <span className="service-emails-list-attention" role="status">
                  <span className="service-emails-list-attention-dot" aria-hidden />
                  {pendingInView === 1
                    ? "1 correo necesita servicio"
                    : `${pendingInView} correos necesitan servicio`}
                </span>
              ) : (
                <span className="service-emails-list-ok">Sin pendientes de servicio en esta vista</span>
              )}
            </div>
          </div>
          {hasFilters && (
            <button className="service-emails-clear-filters" onClick={clearFilters} type="button">
              Limpiar filtros
            </button>
          )}
        </div>

        <div className="service-emails-filters-row">
          <div className="service-emails-search-wrap">
            <input
              id="service-email-search"
              type="text"
              className="service-emails-search-input"
              placeholder="Buscar por asunto, emisor, origen, destino o ID…"
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
            />
          </div>

          <div className="service-emails-company-filter">
            <BaseSelect
              label="Empresa"
              name="company_id"
              value={filters.company_id}
              placeholder="Todas las empresas"
              options={companies}
              hideLabel
              size="compact"
              searchable
              onChange={(event) => updateFilter("company_id", event.target.value)}
            />
          </div>

          <div className="service-emails-service-id-filter">
            <input
              id="service-email-service-id"
              type="text"
              inputMode="numeric"
              className="service-emails-search-input"
              placeholder="ID de servicio"
              value={filters.service_id || ""}
              onChange={(event) =>
                updateFilter("service_id", event.target.value.replace(/\D/g, ""))
              }
            />
          </div>

          <div className="service-emails-date-filters" aria-label="Filtrar por fechas">
            <label className="service-emails-date-field">
              <span>Llegó el correo</span>
              <input
                type="date"
                value={filters.received_date}
                onChange={(event) => updateFilter("received_date", event.target.value)}
              />
            </label>
            <label className="service-emails-date-field">
              <span>Día del servicio</span>
              <input
                type="date"
                value={filters.request_date}
                onChange={(event) => updateFilter("request_date", event.target.value)}
              />
            </label>
          </div>

          <div className="service-emails-status-filters" role="group" aria-label="Filtrar por estado">
            {(Object.keys(STATUS_LABELS) as ServiceEmailStatus[]).map((status) => {
              const active = filters.status === status;
              return (
                <button
                  key={status}
                  type="button"
                  className={`service-emails-status-chip ${status.toLowerCase()}${active ? " is-active" : ""}`}
                  aria-pressed={active}
                  onClick={() => updateFilter("status", active ? "" : status)}
                >
                  {STATUS_LABELS[status]}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="service-emails-table-shell">
        <table className="service-emails-table-modern service-emails-table-v2">
          <thead>
            <tr>
              <th>Correo</th>
              <th>Llegó el correo</th>
              <th>Día / hora del servicio</th>
              <th>Solicitudes</th>
              <th>Servicios</th>
              <th>Ruta</th>
              <th>Cobertura</th>
              <th className="col-actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="service-emails-state-cell">
                  <div className="service-emails-loading">
                    <div className="spinner-pro" />
                    <span>Cargando correos…</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={8} className="service-emails-state-cell">
                  <div className="service-emails-empty">
                    <p>No se encontraron correos</p>
                    <span>
                      {hasFilters ? "Intenta ajustar los filtros" : "Aún no hay correos registrados"}
                    </span>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item) => {
                const received = formatReceived(item.received_at);
                const solicitudesCount = item.solicitudes_count ?? 0;
                const pendingSolicitudes = item.pending_solicitudes_count ?? 0;
                const coveredSolicitudes = Math.max(0, solicitudesCount - pendingSolicitudes);
                const hasMultipleSolicitudes =
                  Boolean(item.has_multiple_requests) || solicitudesCount > 1;
                const hasPendingService =
                  Boolean(item.has_pending_service) || pendingSolicitudes > 0;
                const hasRoute = Boolean(item.origin_text || item.destination_text);
                const notesCount = item.notes_count ?? 0;
                const serviceDay = filters.request_date || item.request_date;
                const coverageLabel = hasPendingService
                  ? pendingSolicitudes === 1
                    ? "Falta agendar servicio"
                    : `Faltan ${pendingSolicitudes} por agendar`
                  : "Servicios agendados";
                const coverageDetail = hasPendingService
                  ? `${coveredSolicitudes} de ${Math.max(solicitudesCount, 1)} listas`
                  : `${solicitudesCount} solicitud${solicitudesCount !== 1 ? "es" : ""} agendada${solicitudesCount !== 1 ? "s" : ""}`;
                const solicitudesLabel =
                  solicitudesCount === 0
                    ? "Sin solicitudes"
                    : solicitudesCount === 1
                      ? "1 solicitud"
                      : `${solicitudesCount} solicitudes`;
                const solicitudesProgress =
                  solicitudesCount === 0
                    ? "—"
                    : `${coveredSolicitudes} de ${solicitudesCount} listas`;
                const associatedServiceIds = Array.from(
                  new Set(
                    (item.solicitudes_resumen ?? [])
                      .flatMap((s) => s.service_ids ?? [])
                      .concat(item.service_id ? [item.service_id] : [])
                  )
                ).sort((a, b) => a - b);

                return (
                  <tr
                    key={item.id}
                    className={`service-emails-row${
                      hasPendingService
                        ? " service-emails-row--needs-service"
                        : " service-emails-row--covered"
                    }`}
                    onClick={() => onEdit(item)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onEdit(item);
                      }
                    }}
                    tabIndex={0}
                    role="link"
                    aria-label={`Abrir correo ${item.subject}`}
                  >
                    <td className="col-mail">
                      <div className="mail-cell mail-cell--v2">
                        <div className="mail-cell-top">
                          <span className="mail-cell-id">#{item.id}</span>
                          <label
                            className="mail-cell-status"
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                          >
                            <span className="visually-hidden">Estado del correo</span>
                            <select
                              className={`mail-cell-workflow mail-cell-workflow--select mail-cell-workflow--${item.status.toLowerCase()}`}
                              value={item.status}
                              aria-label={`Cambiar estado del correo #${item.id}`}
                              onChange={(event) => {
                                const next = event.target.value as ServiceEmailStatus;
                                if (next !== item.status) {
                                  onStatusChange(item, next);
                                }
                              }}
                            >
                              {(Object.keys(STATUS_LABELS) as ServiceEmailStatus[]).map((status) => (
                                <option key={status} value={status}>
                                  {STATUS_LABELS[status]}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <span className="mail-cell-subject" title={item.subject}>
                          {item.subject}
                        </span>
                        <span className="mail-cell-sender" title={item.sender_email}>
                          <span className="mail-cell-sender-name">
                            {item.sender_name || "Sin nombre"}
                          </span>
                          <span className="mail-cell-sender-email">{item.sender_email}</span>
                        </span>
                        {notesCount > 0 ? (
                          <span className="mail-cell-notes">
                            {notesCount} comentario{notesCount !== 1 ? "s" : ""}
                          </span>
                        ) : null}
                      </div>
                    </td>

                    <td className="col-received">
                      <div
                        className="email-when email-when--inbox"
                        title="Cuándo llegó el correo (no es la fecha del servicio)"
                      >
                        <span className="email-when-label">Correo</span>
                        <span className="email-when-line">
                          <span className="email-when-date">{received.date}</span>
                          <span className="email-when-sep" aria-hidden>
                            ·
                          </span>
                          <span className="email-when-time">{received.time}</span>
                        </span>
                      </div>
                    </td>

                    <td className="col-service">
                      <div
                        className="email-departure"
                        title="Fecha y hora en que sale el servicio"
                      >
                        <span className="email-departure-label">Servicio</span>
                        <strong className="email-departure-date">
                          {formatCalendarDay(serviceDay)}
                        </strong>
                        <span className="email-departure-time">
                          Sale a las {formatTime(item.request_time)}
                        </span>
                        {hasMultipleSolicitudes ? (
                          <span className="email-departure-multi">
                            Varias solicitudes
                            {solicitudesCount > 1 ? ` (${solicitudesCount})` : ""}
                          </span>
                        ) : null}
                        <span className="email-departure-pax">
                          {item.passenger_count} pasajero{item.passenger_count !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </td>

                    <td className="col-solicitudes">
                      <div
                        className={`email-solicitudes-meter${
                          hasPendingService ? " is-attention" : " is-ok"
                        }${hasMultipleSolicitudes ? " is-multi" : ""}`}
                      >
                        <strong className="email-solicitudes-total">{solicitudesLabel}</strong>
                        <span className="email-solicitudes-progress">{solicitudesProgress}</span>
                        <span
                          className={`email-solicitudes-status${
                            pendingSolicitudes > 0 ? " is-pending" : " is-complete"
                          }`}
                        >
                          {pendingSolicitudes > 0
                            ? `${pendingSolicitudes} pendiente${pendingSolicitudes !== 1 ? "s" : ""}`
                            : solicitudesCount > 0
                              ? "Completa"
                              : "—"}
                        </span>
                      </div>
                    </td>

                    <td className="col-services">
                      {associatedServiceIds.length > 0 ? (
                        <div className="email-services-ids" title={associatedServiceIds.map((id) => `#${id}`).join(", ")}>
                          {associatedServiceIds.slice(0, 3).map((id) => (
                            <span key={id} className="email-services-id-badge">
                              #{id}
                            </span>
                          ))}
                          {associatedServiceIds.length > 3 ? (
                            <span className="email-services-id-more">
                              +{associatedServiceIds.length - 3}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="route-empty">Sin servicio</span>
                      )}
                    </td>

                    <td className="col-route">
                      {hasRoute ? (
                        <div
                          className="email-route"
                          title={`${item.origin_text} → ${item.destination_text}`}
                        >
                          <span>{item.origin_text || "—"}</span>
                          <span className="email-route-arrow" aria-hidden>
                            →
                          </span>
                          <span>{item.destination_text || "—"}</span>
                        </div>
                      ) : (
                        <span className="route-empty">Sin ruta</span>
                      )}
                    </td>

                    <td className="col-coverage">
                      <div
                        className={`email-coverage${
                          hasPendingService ? " email-coverage--needs" : " email-coverage--ok"
                        }`}
                        role="status"
                      >
                        <span className="email-coverage-pulse" aria-hidden />
                        <div>
                          <strong>{coverageLabel}</strong>
                          <span>{coverageDetail}</span>
                        </div>
                      </div>
                    </td>

                    <td className="col-actions">
                      <div
                        className="srv-actions"
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <BaseTableAction
                          icon={EditIcon}
                          title="Abrir correo"
                          onClick={() => onEdit(item)}
                        />
                        <BaseTableAction
                          icon={CommentIcon}
                          title="Agregar comentario"
                          onClick={() => onComment(item)}
                        />
                        {item.status === "PENDIENTE" ? (
                          <BaseTableAction
                            icon={CheckIcon}
                            title="Marcar como agregado"
                            onClick={() => onStatusChange(item, "AGREGADO")}
                          />
                        ) : (
                          <BaseTableAction
                            icon={UndoIcon}
                            title="Volver a pendiente"
                            onClick={() => onStatusChange(item, "PENDIENTE")}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default React.memo(ServiceEmailsTable);
